import { createApiClient } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';

/** Per-license setup/confirmation status. Row absence on the server = 'not_set_up'. */
export type ComplianceRequirementStatus =
  | 'not_set_up'
  | 'unconfirmed'
  | 'modified'
  | 'confirmed';

export type ReportingPeriodTypeDto =
  | 'calendar_based'
  | 'birthdate_based'
  | 'license_date_based';

/**
 * How a license's compliance is evaluated. 'home_state_reciprocity' derives
 * status from the user's primary (principal place of business) license.
 */
export type ComplianceMode = 'standard' | 'home_state_reciprocity';

/** Why reciprocity can't be elected on a license right now. */
export type ReciprocityBlockedReason =
  | 'state_not_allowed'
  | 'is_primary'
  | 'no_primary'
  | 'primary_same_state'
  | 'primary_not_set_up';

export type SubjectRequirementTargetKind =
  | 'technical'
  | 'non_technical'
  | 'category_set'
  | 'manual'
  | 'total';

export type SubjectRequirementScope = 'total_period' | 'per_year';

export type SubjectRequirementSource = 'state_prefill' | 'user';

/**
 * One tracked subject requirement (recurring per-period category minimum,
 * a total-hours minimum like a state yearly minimum, or a manually attested
 * requirement).
 */
export interface SubjectRequirementDto {
  id: string;
  name: string;
  targetKind: SubjectRequirementTargetKind;
  scope: SubjectRequirementScope;
  requiredHours: number;
  source: SubjectRequirementSource;
  categoryIds: number[];
  categoryNames: string[];
  /** Manual target kind only: when the user attested fulfillment (null = not fulfilled). */
  fulfilledAt?: string | null;
}

/**
 * A conditional state subject requirement the user can opt into tracking
 * (e.g. PA's 24-hour Accounting & Auditing minimum for attest practitioners).
 * Never auto-prefilled.
 */
export interface ConditionalSubjectSuggestionDto {
  name: string;
  /** The condition under which the requirement applies. */
  description?: string | null;
  targetKind: SubjectRequirementTargetKind;
  scope: SubjectRequirementScope;
  requiredHours: number;
  categoryIds: number[];
  categoryNames: string[];
}

/** Request body for creating or updating a subject requirement. */
export interface UpsertSubjectRequirementDto {
  /** Optional display name; defaults server-side from the target. Required for 'manual'. */
  name?: string | null;
  targetKind: SubjectRequirementTargetKind;
  scope: SubjectRequirementScope;
  /** Must be 0 when targetKind is 'manual' (fulfillment is a check-off, not an hours target). */
  requiredHours: number;
  /** Required (non-empty) when targetKind is 'category_set'; must be empty otherwise. */
  categoryIds: number[];
}

/**
 * One evidence link on a manual subject requirement. Evidence is optional
 * supporting material — fulfillment authority is the requirement's check-off.
 */
export interface SubjectRequirementEvidenceDto {
  id: string;
  registrationId?: string | null;
  thirdPartyCreditGroupId?: string | null;
  /** Course title (platform) or activity title (third-party). */
  title: string;
  dateEarned?: string | null;
  hours?: number | null;
}

/**
 * Request body for linking a credit as evidence to a manual subject requirement.
 * Exactly one of registrationId / thirdPartyCreditGroupId must be set.
 */
export interface AddSubjectRequirementEvidenceDto {
  registrationId?: string | null;
  thirdPartyCreditGroupId?: string | null;
}

/**
 * One credit limit (cap) on a license, e.g. "max 20 hours self-study per
 * period" or "max 24 hours personal development". A limit caps either delivery
 * methods or fields of study — exactly one basis per limit. Exceeding a cap is
 * never a violation — excess hours simply stop counting toward the license's
 * total requirement.
 */
export interface CreditLimitDto {
  id: string;
  name: string;
  /** Delivery-method tokens the cap applies to (see utils/deliveryMethods); empty for field-of-study limits. */
  deliveryMethods: string[];
  /** Education-category ids the cap applies to; empty for delivery-method limits. */
  educationCategoryIds: number[];
  /** Display names for educationCategoryIds, resolved server-side (same order). */
  educationCategoryNames: string[];
  /** Absolute cap in hours. At least one of maxHours/maxPercent is set. */
  maxHours?: number | null;
  /** Percentage cap resolved against the license's required total hours at calc time. */
  maxPercent?: number | null;
  scope: SubjectRequirementScope;
  source: SubjectRequirementSource;
}

/** Request body for creating or updating a credit limit. */
export interface UpsertCreditLimitDto {
  /** Optional display name; defaults server-side from the selected basis. */
  name?: string | null;
  /** Empty when using the field-of-study basis. */
  deliveryMethods: string[];
  /** Null/empty when using the delivery-method basis. Exactly one basis must be non-empty. */
  educationCategoryIds?: number[] | null;
  maxHours?: number | null;
  maxPercent?: number | null;
  scope: SubjectRequirementScope;
}

export interface LicenseComplianceRequirementDto {
  totalHours: number;
  ethicsHours: number;
  periodStart: string;
  periodEnd: string;
  prefilledTotalHours?: number | null;
  prefilledEthicsHours?: number | null;
  prefilledPeriodStart?: string | null;
  prefilledPeriodEnd?: string | null;
  /** User-confirmed carry-over hours (null = no entry; 0 = explicit "none"). */
  carryoverHours?: number | null;
  /**
   * The periodStart the carry-over was saved against. Only counts while it
   * equals the current periodStart — a mismatch means the value is stale.
   */
  carryoverPeriodStart?: string | null;
  confirmedAt?: string | null;
}

/** One labeled section of the full NASBA state requirements reference text. */
export interface StateReferenceSectionDto {
  label: string;
  text: string;
}

export interface LicenseSetupEntryDto {
  licenseId: string;
  stateCode: string;
  stateName?: string | null;
  licenseNumber?: string | null;
  issueDate?: string | null;
  isPrimary: boolean;
  status: ComplianceRequirementStatus;
  complianceMode: ComplianceMode;
  /** When the user attested the reciprocity election (null when standard). */
  reciprocityElectedAt?: string | null;
  /** True when the reciprocity eligibility gate currently passes for this license. */
  reciprocityEligible: boolean;
  /** Set when ineligible — lets the UI explain why the toggle is unavailable. */
  reciprocityBlockedReason?: ReciprocityBlockedReason | null;
  /** True when the state honors reciprocity but still requires its own ethics hours. */
  ethicsCarveoutApplies: boolean;
  /** Verified summary of the state's reciprocity rule (citation notes). */
  reciprocityNotesText?: string | null;
  requirement?: LicenseComplianceRequirementDto | null;
  /**
   * Engine-computed carry-over suggestion: prior-period countable hours in
   * excess of this period's required total, clamped to the state cap when
   * seeded. Null when there are no prior-period credits. Display only.
   */
  suggestedCarryoverHours?: number | null;
  /** State's max carry-over hours (soft cap: warn, don't block). Null = no verified rule. */
  carryoverMaxHours?: number | null;
  /** False when the state has no seeded requirement data — prefill is unavailable. */
  hasStateData: boolean;
  reportingPeriodType?: ReportingPeriodTypeDto | null;
  /**
   * Anchor the state's period type needs but the user hasn't provided:
   * 'birth_date' | 'issue_date' | null (null = anchor present or not needed).
   * While set, prefilled windows are calendar-based estimates.
   */
  missingPeriodAnchor?: 'birth_date' | 'issue_date' | null;
  otherSubjectAreaRequirements?: string | null;
  creditLimitationsText?: string | null;
  creditCalculationText?: string | null;
  otherPoliciesText?: string | null;
  /**
   * Full NASBA state requirements text split into labeled sections.
   * When non-empty this supersedes the individual *Text excerpt fields above.
   */
  referenceSections?: StateReferenceSectionDto[];
  sourceStateDataRevisionDate?: string | null;
  /** Current revision date of the state reference data shown in the read-only reference section. */
  stateDataRevisionDate?: string | null;
  /**
   * True when this state's seeded requirement data was re-applied after the user
   * last reviewed (prefilled/edited/confirmed) this license's requirements.
   * Prompts a re-review; always false for unconfirmed rows.
   */
  stateDataUpdatedSinceReview?: boolean;
  /** Tracked subject requirements (state-prefilled + user-added), ordered for display. */
  subjectRequirements: SubjectRequirementDto[];
  /** Conditional state requirements offered as opt-in suggestions. */
  conditionalSubjectSuggestions: ConditionalSubjectSuggestionDto[];
  /** Credit limits (delivery-method caps, state-prefilled + user-added), ordered for display. */
  creditLimits: CreditLimitDto[];
}

export interface ComplianceSetupStateDto {
  licenses: LicenseSetupEntryDto[];
}

export interface UpdateLicenseComplianceRequirementDto {
  totalHours: number;
  ethicsHours: number;
  periodStart: string;
  periodEnd: string;
  confirm: boolean;
  /**
   * Omit (undefined) to leave the stored carry-over unchanged — never
   * "helpfully" send 0. A value ≥ 0 sets it and anchors it to the period being
   * saved; explicit 0 means "none" (suppresses the apply-suggestion banner).
   */
  carryoverHours?: number | null;
}

/** One 12-month segment of a reporting period for a per_year requirement. */
export interface YearBucketDto {
  index: number;
  start: string;
  end: string;
  requiredHours: number;
  earnedHours: number;
  isMet: boolean;
  /** True when the bucket has fully ended; only elapsed buckets can fail. */
  isElapsed: boolean;
}

/** Fulfillment progress for one subject requirement. */
export interface SubjectRequirementFulfillmentDto {
  id: string;
  name: string;
  targetKind: SubjectRequirementTargetKind;
  scope: SubjectRequirementScope;
  source: SubjectRequirementSource;
  requiredHours: number;
  earnedHours: number;
  remainingHours: number;
  /** Display names of tracked education categories (category_set target kind only). */
  categoryNames: string[];
  isMet: boolean;
  /** Populated for per_year scope only. */
  yearBuckets?: YearBucketDto[] | null;
  /** Manual target kind only: when the user attested fulfillment (null = not fulfilled this period). */
  fulfilledAt?: string | null;
  /** Manual target kind only: linked credit evidence. */
  evidence?: SubjectRequirementEvidenceDto[];
}

/** One 12-month segment of a reporting period for a per_year credit limit. */
export interface CreditLimitYearBucketDto {
  index: number;
  start: string;
  end: string;
  /** Resolved cap for this bucket (null = no enforceable cap). */
  effectiveCapHours?: number | null;
  usedHours: number;
  excessHours: number;
  isExceeded: boolean;
}

/** Usage/excess for one credit limit within the license's current period. */
export interface CreditLimitFulfillmentDto {
  id: string;
  name: string;
  deliveryMethods: string[];
  /** Non-empty iff this limit caps fields of study instead of delivery methods. */
  educationCategoryIds: number[];
  /** Display names for educationCategoryIds, resolved server-side. */
  educationCategoryNames: string[];
  maxHours?: number | null;
  maxPercent?: number | null;
  scope: SubjectRequirementScope;
  source: SubjectRequirementSource;
  /**
   * The cap actually enforced (stricter of maxHours and the resolved percent
   * cap); null when no cap can resolve (e.g. percent-only with no total set).
   */
  effectiveCapHours?: number | null;
  /** Raw in-period hours earned under this limit's basis (delivery methods or fields of study). */
  usedHours: number;
  /** Hours over the cap — informational, never a violation. */
  excessHours: number;
  /** usedHours minus excessHours: what actually counts toward the total. */
  countableHours: number;
  isExceeded: boolean;
  /** Populated for per_year scope only. */
  yearBuckets?: CreditLimitYearBucketDto[] | null;
}

export interface LicenseFulfillmentDto {
  licenseId: string;
  stateCode: string;
  stateName?: string | null;
  isPrimary: boolean;
  status: ComplianceRequirementStatus;
  /**
   * 'home_state_reciprocity' = derived entry: status mirrors the primary license
   * (see reciprocitySourceLicenseId); hour/subject/limit collections are empty,
   * except carve-out states which still evaluate their own ethics hours.
   */
  complianceMode: ComplianceMode;
  /** The primary license this derived entry mirrors (computed at read time, never stored). */
  reciprocitySourceLicenseId?: string | null;
  /**
   * True when reciprocity is elected but its gate failed at read time (e.g. the
   * primary was un-confirmed) — the entry fell back to standard evaluation.
   */
  reciprocityGateFailed: boolean;
  /**
   * Standard-mode entries only: this license passes the reciprocity gate but
   * hasn't elected it — drives the discovery hint on the Reporting page.
   * Always false for elected entries and the primary license.
   */
  reciprocityEligible: boolean;
  /** True when this derived entry still requires the state's own ethics hours. */
  ethicsCarveoutApplies: boolean;
  /**
   * Anchor the state's period type needs but the user hasn't provided:
   * 'birth_date' | 'issue_date' | null (null = anchor present or not needed).
   */
  missingPeriodAnchor?: 'birth_date' | 'issue_date' | null;
  periodStart: string;
  periodEnd: string;
  requiredTotalHours: number;
  requiredEthicsHours: number;
  earnedTotalHours: number;
  earnedEthicsHours: number;
  /**
   * Hours counting toward the total after credit limits are applied
   * (equals earnedTotalHours when no limit is exceeded). Drives
   * remaining/percent/compliance.
   */
  countableTotalHours: number;
  /**
   * Carry-over hours currently applied (0 when none, stale, or derived).
   * Already included in countableTotalHours; earnedTotalHours stays raw.
   */
  carryoverHours?: number;
  /**
   * True when a carry-over entry exists for the current period — including an
   * explicit 0 ("none"). Suppresses the apply-suggestion banner.
   */
  hasCarryoverEntry?: boolean;
  /**
   * Engine-computed carry-over suggestion (prior-period countable excess,
   * state-capped). Null when there are no prior-period credits.
   */
  suggestedCarryoverHours?: number | null;
  /** State's max carry-over hours (soft cap: warn, don't block). Null = no verified rule. */
  carryoverMaxHours?: number | null;
  remainingTotalHours: number;
  remainingEthicsHours: number;
  percentComplete: number;
  isCompliant: boolean;
  /**
   * True when this state's seeded requirement data was re-applied after the user
   * last reviewed (prefilled/edited/confirmed) this license's requirements.
   */
  stateDataUpdatedSinceReview?: boolean;
  /** Progress per tracked subject requirement. */
  subjectRequirements: SubjectRequirementFulfillmentDto[];
  /** Usage per credit limit (delivery-method cap). */
  creditLimits: CreditLimitFulfillmentDto[];
  /**
   * In-period third-party hours with no classifiable delivery method. Exempt
   * from caps; used to nudge the user to classify when limits exist.
   */
  unclassifiedThirdPartyHours: number;
  unclassifiedThirdPartyCreditCount: number;
}

export interface MultiLicenseFulfillmentDto {
  licenses: LicenseFulfillmentDto[];
  setUpCount: number;
  totalActiveLicenses: number;
}

class LicenseComplianceService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.users);
  }

  /** Current per-license compliance setup state (statuses + reference text). */
  async getSetupState(): Promise<ComplianceSetupStateDto> {
    const response = await this.api.get<ComplianceSetupStateDto>(
      '/api/users/me/compliance/setup'
    );
    return response.data;
  }

  /**
   * Prefills the requirement row for one license from seeded state data.
   * Only overwrites a row that is still 'unconfirmed'; never clobbers
   * modified/confirmed rows. Returns the refreshed setup state for all licenses.
   */
  async prefillLicense(licenseId: string): Promise<ComplianceSetupStateDto> {
    const response = await this.api.post<ComplianceSetupStateDto>(
      `/api/users/me/compliance/licenses/${licenseId}/prefill`
    );
    return response.data;
  }

  /** Updates (and optionally confirms) the requirement for one license. */
  async updateRequirement(
    licenseId: string,
    dto: UpdateLicenseComplianceRequirementDto
  ): Promise<LicenseComplianceRequirementDto> {
    const response = await this.api.put<LicenseComplianceRequirementDto>(
      `/api/users/me/compliance/licenses/${licenseId}`,
      dto
    );
    return response.data;
  }

  /** Adds a subject requirement to a license. */
  async addSubjectRequirement(
    licenseId: string,
    dto: UpsertSubjectRequirementDto
  ): Promise<SubjectRequirementDto> {
    const response = await this.api.post<SubjectRequirementDto>(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements`,
      dto
    );
    return response.data;
  }

  /** Updates a subject requirement on a license. */
  async updateSubjectRequirement(
    licenseId: string,
    requirementId: string,
    dto: UpsertSubjectRequirementDto
  ): Promise<SubjectRequirementDto> {
    const response = await this.api.put<SubjectRequirementDto>(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements/${requirementId}`,
      dto
    );
    return response.data;
  }

  /** Deletes a subject requirement from a license. */
  async deleteSubjectRequirement(
    licenseId: string,
    requirementId: string
  ): Promise<void> {
    await this.api.delete(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements/${requirementId}`
    );
  }

  /**
   * Checks a manual subject requirement off (or un-checks it).
   * The check-off is the sole fulfillment authority for manual requirements.
   */
  async setSubjectRequirementFulfillment(
    licenseId: string,
    requirementId: string,
    fulfilled: boolean
  ): Promise<SubjectRequirementDto> {
    const response = await this.api.put<SubjectRequirementDto>(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements/${requirementId}/fulfillment`,
      { fulfilled }
    );
    return response.data;
  }

  /** Links a credit (platform course or third-party activity) as evidence to a manual requirement. */
  async addSubjectRequirementEvidence(
    licenseId: string,
    requirementId: string,
    dto: AddSubjectRequirementEvidenceDto
  ): Promise<SubjectRequirementEvidenceDto> {
    const response = await this.api.post<SubjectRequirementEvidenceDto>(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements/${requirementId}/evidence`,
      dto
    );
    return response.data;
  }

  /** Removes an evidence link from a manual requirement. Never un-fulfills it. */
  async removeSubjectRequirementEvidence(
    licenseId: string,
    requirementId: string,
    evidenceId: string
  ): Promise<void> {
    await this.api.delete(
      `/api/users/me/compliance/licenses/${licenseId}/subject-requirements/${requirementId}/evidence/${evidenceId}`
    );
  }

  /** Adds a credit limit (delivery-method cap) to a license. */
  async addCreditLimit(
    licenseId: string,
    dto: UpsertCreditLimitDto
  ): Promise<CreditLimitDto> {
    const response = await this.api.post<CreditLimitDto>(
      `/api/users/me/compliance/licenses/${licenseId}/credit-limits`,
      dto
    );
    return response.data;
  }

  /** Updates a credit limit on a license. */
  async updateCreditLimit(
    licenseId: string,
    limitId: string,
    dto: UpsertCreditLimitDto
  ): Promise<CreditLimitDto> {
    const response = await this.api.put<CreditLimitDto>(
      `/api/users/me/compliance/licenses/${licenseId}/credit-limits/${limitId}`,
      dto
    );
    return response.data;
  }

  /** Deletes a credit limit from a license. */
  async deleteCreditLimit(licenseId: string, limitId: string): Promise<void> {
    await this.api.delete(
      `/api/users/me/compliance/licenses/${licenseId}/credit-limits/${limitId}`
    );
  }

  /**
   * Elects or revokes home-state reciprocity on a license. Election is validated
   * against the eligibility gate server-side (400 with the blocked reason on failure).
   */
  async setComplianceMode(
    licenseId: string,
    mode: ComplianceMode
  ): Promise<LicenseSetupEntryDto> {
    const response = await this.api.put<LicenseSetupEntryDto>(
      `/api/users/me/compliance/licenses/${licenseId}/compliance-mode`,
      { mode }
    );
    return response.data;
  }

  /** Per-license fulfillment progress (only set-up licenses are listed). */
  async getFulfillment(): Promise<MultiLicenseFulfillmentDto> {
    const response = await this.api.get<MultiLicenseFulfillmentDto>(
      '/api/users/me/compliance/fulfillment'
    );
    return response.data;
  }
}

export default new LicenseComplianceService();
